import { Component, computed, effect, HostListener, inject, signal, ViewEncapsulation } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ModalService } from '@src/suite/common/components/modal/modal.service';
import { ModalComponent } from '@src/suite/common/components/modal/modal/modal';
import { selectUseMosStartTime } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectMosStorySendDevices } from '@src/suite/common/store/mos/selectors/mos.selectors';
import { selectRundownRecords } from '@src/suite/common/store/records/records.selectors';
import { LiveApiActions } from '@src/suite/live/store/actions/live.actions';
import { DateTime } from 'luxon';

import { DateTimePickerComponent } from '../../../common/controls/date-time-picker/date-time-picker';

@Component({
    selector: 'st-mos-story-send',
    imports: [DateTimePickerComponent, MatButton, MatCheckbox],
    templateUrl: './st-mos-story-send.component.html',
    styleUrl: './st-mos-story-send.component.scss',
    encapsulation: ViewEncapsulation.None,
})
export class StMosStorySendComponent {
    dialogRef = inject<MatDialogRef<ModalComponent>>(MatDialogRef);
    data = inject<{
        guid: string;
    }>(MAT_DIALOG_DATA);

    // injectables
    store = inject(Store);
    modalService = inject(ModalService);

    // store signals
    currentMosStorySendDevices = this.store.selectSignal(selectMosStorySendDevices);
    languageSettings = this.store.selectSignal(selectI18N)();
    useMosStartTime = this.store.selectSignal(selectUseMosStartTime);

    // signals
    readonly localMosDevices = signal<StorySendTarget[]>([]);

    // computed
    readonly sortedMosStorySendDevices = computed(() => {
        return this.currentMosStorySendDevices() ? [...this.currentMosStorySendDevices()].sort((a, b) => a.Description.localeCompare(b.Description)) : [];
    });
    readonly anySelectedMosDevices = computed(() => this.localMosDevices().some(mos => mos.IsChecked));
    //Already selected MOS devices
    readonly mosStorySendTargets = computed(() => this.record()?.MOSStorySendTargets);
    readonly record = computed(() => {
        const allRecords = this.store.selectSignal(selectRundownRecords)();
        return allRecords.find(record => record.Guid === this.data.guid);
    });
    readonly mosControl = computed(() => this.record()?.MosControl);

    constructor() {
        effect(() => {
            if (this.sortedMosStorySendDevices()) {
                this.localMosDevices.set(
                    this.sortedMosStorySendDevices().map(mos => ({
                        Description: mos.Description,
                        MOSID: mos.ID,
                        StartTime: null,
                        IsChecked: !!this.mosStorySendTargets()?.find(i => i.MOSID === mos.ID),
                    }))
                );
            }
        });
    }

    @HostListener('document:keydown.escape')
    public onEscape() {
        this.dialogRef?.close();
    }

    onSelectedChange(mosSelected: StorySendTarget) {
        this.localMosDevices.update(devices => {
            return (
                devices?.map(device => {
                    if (device.MOSID === mosSelected.MOSID) {
                        return {
                            ...device,
                            IsChecked: !device.IsChecked,
                        };
                    }
                    return device;
                }) || []
            );
        });
    }

    onTimeChange(dateTime: DateTime, id: string) {
        this.localMosDevices.update(devices => {
            return (
                devices?.map(device => {
                    if (device.MOSID === id) {
                        return {
                            ...device,
                            StartTime: dateTime,
                        };
                    }
                    return device;
                }) || []
            );
        });
    }

    save() {
        this.store.dispatch(
            LiveApiActions.mosStorySend({
                guid: this.data.guid,
                storySendTargets: this.localMosDevices().filter(mos => mos.IsChecked),
            })
        );
        this.dialogRef?.close();
    }
}
